import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Card, CardHeader, CardContent } from '../Common/Card';
import MainLayout from '../Layout/MainLayout';

const RFQWorkflowDebugLogs = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get RFQ ID and run ID from URL
  const rfqId = searchParams.get('rfqId');
  const runId = searchParams.get('runId');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('steps'); // 'steps', 'emails', 'chat', 'api_calls'
  const [stepExecutions, setStepExecutions] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [apiCallLogs, setApiCallLogs] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [summary, setSummary] = useState({});
  const [rfqNumber, setRfqNumber] = useState(null);
  const [parentRunId, setParentRunId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    input: false,
    output: true,
    metadata: true
  });

  // Fetch logs on mount
  useEffect(() => {
    fetchLogs();
  }, [rfqId, runId]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = Cookies.get('sankalvax_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (rfqId) {
        // Fetch all debug logs for RFQ
        const response = await fetch(`/api/rfq-workflow/rfq/${rfqId}/debug-logs`, { headers });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setStepExecutions(data.step_executions || []);
            setEmailLogs(data.email_logs || []);
            setChatMessages(data.chat_messages || []);
            setApiCallLogs(data.api_call_logs || []);
            setSummary(data.summary || {});
            setRfqNumber(data.rfq_number);
            setParentRunId(data.parent_run_id);

            // Select first item if available
            if (data.step_executions?.length > 0) {
              setSelectedItem(data.step_executions[0]);
            }
          }
        } else {
          throw new Error('Failed to fetch debug logs');
        }
      } else if (runId) {
        // Fetch execution steps for specific run
        const response = await fetch(`/api/rfq-workflow/executions/${runId}/steps`, { headers });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setStepExecutions(data.steps || []);
            setSummary({ total_steps: data.total_steps });
            if (data.steps?.length > 0) {
              setSelectedItem(data.steps[0]);
            }
          }
        }
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatJson = (obj) => {
    try {
      if (typeof obj === 'string') {
        try {
          return JSON.stringify(JSON.parse(obj), null, 2);
        } catch {
          return obj;
        }
      }
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300';
      case 'failed': return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300';
      case 'running': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300';
      case 'pending': return 'text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-300';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDirectionBadge = (direction) => {
    if (direction === 'outbound') {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  };

  const getMessageTypeBadge = (type) => {
    switch (type) {
      case 'user': return 'bg-blue-100 text-blue-800';
      case 'assistant': return 'bg-purple-100 text-purple-800';
      case 'tool_call': return 'bg-orange-100 text-orange-800';
      case 'tool_result': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <MainLayout title="RFQ Workflow Debug Logs">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading RFQ Debug Logs...</div>
        </div>
      </MainLayout>
    );
  }

  const getWorkflowTypeBadge = (type) => {
    if (type === 'parent') {
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    }
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  };

  const renderStepsList = () => (
    <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
      {stepExecutions.map((step, index) => (
        <div
          key={step.id || index}
          onClick={() => setSelectedItem(step)}
          className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
            selectedItem?.id === step.id ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-500' : ''
          }`}
        >
          {/* Workflow Type Badge */}
          {step.workflow_type && (
            <div className="mb-2">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getWorkflowTypeBadge(step.workflow_type)}`}>
                {step.workflow_type === 'parent' ? '🔷 Parent' : '🟢 Child'}
              </span>
            </div>
          )}
          <div className="flex justify-between items-start mb-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {step.step_name}
            </span>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(step.status)}`}>
              {step.status}
            </span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
            <div>Type: {step.step_type}</div>
            <div>{formatDuration(step.execution_time_ms)}</div>
            {step.tokens_used > 0 && <div>{step.tokens_used} tokens</div>}
            <div className="text-gray-400">{formatTimestamp(step.started_at)}</div>
          </div>
          {step.error_message && (
            <div className="mt-2 text-xs text-red-600 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded truncate">
              {step.error_message.substring(0, 50)}...
            </div>
          )}
        </div>
      ))}
      {stepExecutions.length === 0 && (
        <div className="p-4 text-center text-gray-500">No step executions found</div>
      )}
    </div>
  );

  const renderEmailsList = () => (
    <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
      {emailLogs.map((email, index) => (
        <div
          key={email.id || index}
          onClick={() => setSelectedItem(email)}
          className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
            selectedItem?.id === email.id ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-500' : ''
          }`}
        >
          <div className="flex justify-between items-start mb-2">
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getDirectionBadge(email.direction)}`}>
              {email.direction === 'outbound' ? '📤 Sent' : '📥 Received'}
            </span>
            <span className="text-xs text-gray-500">
              {email.classification}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate mb-1">
            {email.email_subject}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <div>{email.direction === 'outbound' ? `To: ${email.email_to}` : `From: ${email.email_from}`}</div>
            <div>Outcome: <span className="font-medium">{email.outcome}</span></div>
            <div>{formatTimestamp(email.analyzed_at)}</div>
          </div>
        </div>
      ))}
      {emailLogs.length === 0 && (
        <div className="p-4 text-center text-gray-500">No email logs found</div>
      )}
    </div>
  );

  const renderChatList = () => (
    <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
      {chatMessages.map((msg, index) => (
        <div
          key={msg.message_id || index}
          onClick={() => setSelectedItem(msg)}
          className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
            selectedItem?.message_id === msg.message_id ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-500' : ''
          }`}
        >
          <div className="flex justify-between items-start mb-2">
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getMessageTypeBadge(msg.message_type)}`}>
              {msg.message_type}
            </span>
            {msg.tool_name && (
              <span className="text-xs text-purple-600 font-medium">
                🔧 {msg.tool_name}
              </span>
            )}
          </div>
          <div className="text-sm text-gray-900 dark:text-gray-100 line-clamp-2">
            {msg.message_content?.substring(0, 100)}...
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {formatTimestamp(msg.created_at)}
          </div>
        </div>
      ))}
      {chatMessages.length === 0 && (
        <div className="p-4 text-center text-gray-500">No chat messages found</div>
      )}
    </div>
  );

  const renderStepDetails = () => {
    if (!selectedItem || !selectedItem.step_name) return null;

    return (
      <div className="space-y-4">
        {/* Step Information */}
        <Card>
          <CardHeader>
            <h3 className="font-medium text-blue-700 dark:text-blue-300">Step Information</h3>
          </CardHeader>
          <CardContent>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="font-medium text-gray-700 dark:text-gray-300">Step Name:</label>
                  <div className="text-blue-800 dark:text-blue-200 font-mono text-sm bg-white dark:bg-gray-800 px-2 py-1 rounded mt-1">
                    {selectedItem.step_name}
                  </div>
                </div>
                <div>
                  <label className="font-medium text-gray-700 dark:text-gray-300">Step Type:</label>
                  <div className="text-blue-800 dark:text-blue-200 font-mono">{selectedItem.step_type}</div>
                </div>
                <div>
                  <label className="font-medium text-gray-700 dark:text-gray-300">Status:</label>
                  <div className={`inline-block px-2 py-1 rounded text-sm font-medium ${getStatusColor(selectedItem.status)}`}>
                    {selectedItem.status}
                  </div>
                </div>
                <div>
                  <label className="font-medium text-gray-700 dark:text-gray-300">Duration:</label>
                  <div className="text-blue-800 dark:text-blue-200 font-mono">{formatDuration(selectedItem.execution_time_ms)}</div>
                </div>
                {selectedItem.tokens_used > 0 && (
                  <div>
                    <label className="font-medium text-gray-700 dark:text-gray-300">Tokens:</label>
                    <div className="text-blue-800 dark:text-blue-200 font-mono">{selectedItem.tokens_used}</div>
                  </div>
                )}
                {selectedItem.cost > 0 && (
                  <div>
                    <label className="font-medium text-gray-700 dark:text-gray-300">Cost:</label>
                    <div className="text-blue-800 dark:text-blue-200 font-mono">${selectedItem.cost?.toFixed(6)}</div>
                  </div>
                )}
              </div>
              {selectedItem.error_message && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                  <label className="font-medium text-red-700 dark:text-red-300">Error:</label>
                  <div className="text-red-600 dark:text-red-400 text-sm mt-1">{selectedItem.error_message}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Input Data */}
        <Card>
          <CardHeader>
            <button
              onClick={() => toggleSection('input')}
              className="w-full text-left flex justify-between items-center"
            >
              <h3 className="font-medium">Input Data</h3>
              <span>{expandedSections.input ? '▼' : '▶'}</span>
            </button>
          </CardHeader>
          {expandedSections.input && (
            <CardContent>
              <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-4 rounded overflow-x-auto border font-mono max-h-96 overflow-y-auto">
                {formatJson(selectedItem.input_data) || 'No input data'}
              </pre>
            </CardContent>
          )}
        </Card>

        {/* Output Data */}
        <Card>
          <CardHeader>
            <button
              onClick={() => toggleSection('output')}
              className="w-full text-left flex justify-between items-center"
            >
              <h3 className="font-medium">Output Data</h3>
              <span>{expandedSections.output ? '▼' : '▶'}</span>
            </button>
          </CardHeader>
          {expandedSections.output && (
            <CardContent>
              <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-4 rounded overflow-x-auto border font-mono max-h-96 overflow-y-auto">
                {formatJson(selectedItem.output_data) || 'No output data'}
              </pre>
            </CardContent>
          )}
        </Card>
      </div>
    );
  };

  const renderEmailDetails = () => {
    if (!selectedItem || !selectedItem.email_subject) return null;

    return (
      <div className="space-y-4">
        {/* Email Information */}
        <Card>
          <CardHeader>
            <h3 className="font-medium text-green-700 dark:text-green-300">Email Details</h3>
          </CardHeader>
          <CardContent>
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <div className="space-y-3 text-sm">
                <div>
                  <label className="font-medium text-gray-700 dark:text-gray-300">Subject:</label>
                  <div className="text-gray-900 dark:text-gray-100 mt-1">{selectedItem.email_subject}</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-medium text-gray-700 dark:text-gray-300">From:</label>
                    <div className="text-gray-900 dark:text-gray-100">{selectedItem.email_from || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="font-medium text-gray-700 dark:text-gray-300">To:</label>
                    <div className="text-gray-900 dark:text-gray-100">{selectedItem.email_to || 'N/A'}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="font-medium text-gray-700 dark:text-gray-300">Direction:</label>
                    <div className={`inline-block px-2 py-1 rounded text-sm font-medium mt-1 ${getDirectionBadge(selectedItem.direction)}`}>
                      {selectedItem.direction}
                    </div>
                  </div>
                  <div>
                    <label className="font-medium text-gray-700 dark:text-gray-300">Classification:</label>
                    <div className="text-gray-900 dark:text-gray-100">{selectedItem.classification}</div>
                  </div>
                  <div>
                    <label className="font-medium text-gray-700 dark:text-gray-300">Outcome:</label>
                    <div className="text-gray-900 dark:text-gray-100 font-medium">{selectedItem.outcome}</div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Analysis Summary */}
        {selectedItem.analysis_summary && (
          <Card>
            <CardHeader>
              <h3 className="font-medium">Analysis Summary</h3>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-4 rounded">
                {selectedItem.analysis_summary}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Extracted Prices */}
        {selectedItem.extracted_prices && (
          <Card>
            <CardHeader>
              <h3 className="font-medium text-purple-700 dark:text-purple-300">Extracted Prices</h3>
            </CardHeader>
            <CardContent>
              <pre className="text-xs bg-purple-50 dark:bg-purple-900/20 p-4 rounded overflow-x-auto border border-purple-200 dark:border-purple-800 font-mono max-h-96 overflow-y-auto">
                {formatJson(selectedItem.extracted_prices)}
              </pre>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderChatDetails = () => {
    if (!selectedItem || !selectedItem.message_content) return null;

    return (
      <div className="space-y-4">
        {/* Message Information */}
        <Card>
          <CardHeader>
            <h3 className="font-medium text-purple-700 dark:text-purple-300">Message Details</h3>
          </CardHeader>
          <CardContent>
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <label className="font-medium text-gray-700 dark:text-gray-300">Type:</label>
                  <div className={`inline-block px-2 py-1 rounded text-sm font-medium mt-1 ${getMessageTypeBadge(selectedItem.message_type)}`}>
                    {selectedItem.message_type}
                  </div>
                </div>
                {selectedItem.tool_name && (
                  <div>
                    <label className="font-medium text-gray-700 dark:text-gray-300">Tool:</label>
                    <div className="text-purple-600 dark:text-purple-400 font-mono">{selectedItem.tool_name}</div>
                  </div>
                )}
                {selectedItem.tokens_used > 0 && (
                  <div>
                    <label className="font-medium text-gray-700 dark:text-gray-300">Tokens:</label>
                    <div className="text-gray-900 dark:text-gray-100">{selectedItem.tokens_used}</div>
                  </div>
                )}
                <div>
                  <label className="font-medium text-gray-700 dark:text-gray-300">Time:</label>
                  <div className="text-gray-500 text-xs">{formatTimestamp(selectedItem.created_at)}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Message Content */}
        <Card>
          <CardHeader>
            <h3 className="font-medium">Message Content</h3>
          </CardHeader>
          <CardContent>
            <pre className="text-sm bg-gray-50 dark:bg-gray-800 p-4 rounded overflow-x-auto border font-mono max-h-96 overflow-y-auto whitespace-pre-wrap">
              {selectedItem.message_content}
            </pre>
          </CardContent>
        </Card>

        {/* Metadata */}
        {selectedItem.message_metadata && (
          <Card>
            <CardHeader>
              <button
                onClick={() => toggleSection('metadata')}
                className="w-full text-left flex justify-between items-center"
              >
                <h3 className="font-medium text-orange-700 dark:text-orange-300">Metadata (Tool Results)</h3>
                <span>{expandedSections.metadata ? '▼' : '▶'}</span>
              </button>
            </CardHeader>
            {expandedSections.metadata && (
              <CardContent>
                <pre className="text-xs bg-orange-50 dark:bg-orange-900/20 p-4 rounded overflow-x-auto border border-orange-200 dark:border-orange-800 font-mono max-h-96 overflow-y-auto">
                  {formatJson(selectedItem.message_metadata)}
                </pre>
              </CardContent>
            )}
          </Card>
        )}
      </div>
    );
  };

  const renderDetails = () => {
    if (!selectedItem) {
      return (
        <Card>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              Select an item from the list to view details
            </div>
          </CardContent>
        </Card>
      );
    }

    if (activeTab === 'steps') return renderStepDetails();
    if (activeTab === 'emails') return renderEmailDetails();
    if (activeTab === 'chat') return renderChatDetails();
    if (activeTab === 'api_calls') return renderApiCallDetails();
    return null;
  };

  // Render API Calls List
  const renderApiCallsList = () => {
    if (apiCallLogs.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500">
          No API call logs found
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
        {apiCallLogs.map((log, index) => (
          <div
            key={log.id || index}
            onClick={() => setSelectedItem(log)}
            className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
              selectedItem?.id === log.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <span className={`px-2 py-0.5 text-xs rounded ${
                log.step_name?.startsWith('tool:')
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {log.step_name?.replace('ai_chat:', '').replace('tool:', '') || 'API'}
              </span>
              <span className={`px-2 py-0.5 text-xs rounded ${
                log.response_status === 200
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {log.response_status || 'N/A'}
              </span>
            </div>
            <div className="mt-1 text-sm font-medium truncate">
              {log.provider} - {log.model || 'N/A'}
            </div>
            <div className="mt-1 text-xs text-gray-500 flex items-center gap-2">
              <span>{log.duration_ms}ms</span>
              {log.tokens_used > 0 && <span>{log.tokens_used} tokens</span>}
              {log.cost > 0 && <span>${log.cost.toFixed(4)}</span>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render API Call Details
  const renderApiCallDetails = () => {
    if (!selectedItem) {
      return (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            Select an API call to view details
          </CardContent>
        </Card>
      );
    }

    const log = selectedItem;

    return (
      <Card>
        <CardHeader className="flex justify-between items-center">
          <div>
            <h3 className="font-medium">{log.step_name}</h3>
            <p className="text-sm text-gray-500">{log.provider} - {log.model}</p>
          </div>
          <span className={`px-3 py-1 rounded text-sm ${
            log.response_status === 200
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {log.response_status}
          </span>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Metrics */}
          <div className="grid grid-cols-4 gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded">
            <div>
              <div className="text-xs text-gray-500">Duration</div>
              <div className="font-medium">{log.duration_ms}ms</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Tokens</div>
              <div className="font-medium">{log.tokens_used || 0}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Cost</div>
              <div className="font-medium">${(log.cost || 0).toFixed(4)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Time</div>
              <div className="font-medium text-xs">{log.created_at ? new Date(log.created_at).toLocaleString() : 'N/A'}</div>
            </div>
          </div>

          {/* Request Body */}
          <div>
            <button
              onClick={() => setExpandedSections(prev => ({ ...prev, request: !prev.request }))}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {expandedSections.request ? '▼' : '▶'} Request Body
            </button>
            {expandedSections.request && (
              <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-900 rounded text-xs overflow-auto max-h-60">
                {JSON.stringify(log.request_body, null, 2)}
              </pre>
            )}
          </div>

          {/* Response Body */}
          <div>
            <button
              onClick={() => setExpandedSections(prev => ({ ...prev, response: !prev.response }))}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {expandedSections.response ? '▼' : '▶'} Response Body
            </button>
            {expandedSections.response && (
              <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-900 rounded text-xs overflow-auto max-h-60">
                {JSON.stringify(log.response_body, null, 2)}
              </pre>
            )}
          </div>

          {/* Tool Calls (if present) */}
          {log.response_body?.choices?.[0]?.message?.tool_calls && (
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                🔧 Tool Calls
              </div>
              <div className="space-y-2">
                {log.response_body.choices[0].message.tool_calls.map((tc, idx) => (
                  <div key={idx} className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
                    <div className="font-medium text-purple-700 dark:text-purple-300">
                      {tc.function?.name}
                    </div>
                    <pre className="mt-1 text-xs overflow-auto">
                      {JSON.stringify(JSON.parse(tc.function?.arguments || '{}'), null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <MainLayout title="RFQ Workflow Debug Logs">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">RFQ Workflow Debug</span>
            {rfqId && (
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 text-xs rounded-full font-medium">
                RFQ: {rfqId.substring(0, 8)}...
              </span>
            )}
          </div>
          <button
            onClick={handleBack}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
          >
            ← Back
          </button>
        </div>

        {/* Summary */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <h3 className="text-lg font-semibold mb-3 text-blue-800 dark:text-blue-200">
            📋 RFQ {rfqNumber} Debug Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Total Steps:</span>
              <span className="ml-2 font-mono font-semibold text-blue-700 dark:text-blue-300">
                {summary.total_steps || stepExecutions.length}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">🔷 Parent:</span>
              <span className="ml-2 font-mono font-semibold text-purple-700 dark:text-purple-300">
                {summary.parent_steps || 0}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">🟢 Child:</span>
              <span className="ml-2 font-mono font-semibold text-green-700 dark:text-green-300">
                {summary.child_steps || 0}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Email Logs:</span>
              <span className="ml-2 font-mono font-semibold text-green-700 dark:text-green-300">
                {summary.total_emails || emailLogs.length}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">AI Chat:</span>
              <span className="ml-2 font-mono font-semibold text-purple-700 dark:text-purple-300">
                {summary.total_chat_messages || chatMessages.length}
              </span>
            </div>
          </div>
          {parentRunId && (
            <div className="mt-3 text-xs text-gray-500">
              Parent Run ID: <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">{parentRunId}</code>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <Card>
            <CardContent>
              <div className="text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="font-medium">Error loading logs:</div>
                <div className="mt-1 text-sm">{error}</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => { setActiveTab('steps'); setSelectedItem(stepExecutions[0]); }}
            className={`px-4 py-2 font-medium text-sm border-b-2 ${
              activeTab === 'steps'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📊 Workflow Steps ({stepExecutions.length})
          </button>
          <button
            onClick={() => { setActiveTab('emails'); setSelectedItem(emailLogs[0]); }}
            className={`px-4 py-2 font-medium text-sm border-b-2 ${
              activeTab === 'emails'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📧 Email Logs ({emailLogs.length})
          </button>
          <button
            onClick={() => { setActiveTab('chat'); setSelectedItem(chatMessages[0]); }}
            className={`px-4 py-2 font-medium text-sm border-b-2 ${
              activeTab === 'chat'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            💬 AI Chat ({chatMessages.length})
          </button>
          <button
            onClick={() => { setActiveTab('api_calls'); setSelectedItem(apiCallLogs[0]); }}
            className={`px-4 py-2 font-medium text-sm border-b-2 ${
              activeTab === 'api_calls'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🔧 API Calls ({apiCallLogs.length})
          </button>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <h3 className="font-medium">
                  {activeTab === 'steps' && 'Workflow Steps'}
                  {activeTab === 'emails' && 'Email Logs'}
                  {activeTab === 'chat' && 'Chat Messages'}
                  {activeTab === 'api_calls' && 'API Call Logs'}
                </h3>
              </CardHeader>
              <CardContent className="p-0">
                {activeTab === 'steps' && renderStepsList()}
                {activeTab === 'emails' && renderEmailsList()}
                {activeTab === 'chat' && renderChatList()}
                {activeTab === 'api_calls' && renderApiCallsList()}
              </CardContent>
            </Card>
          </div>

          {/* Details */}
          <div className="lg:col-span-2">
            {renderDetails()}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default RFQWorkflowDebugLogs;
